import { Badge, Button, Collapse, Tooltip } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconPencilPlus } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  subtypeIcons,
  typeIcons,
} from 'src/Components/Forms/PracticalInformation/PracticalInformationForm/PracticalInformationCategoryMapping.helper';
import {
  PRACTICAL_INFORMATION_SUGGESTIONS,
  PracticalInformationSuggestion,
  getTypeForSubtype,
} from 'src/Components/PracticalInformation/PracticalInformationSuggestions/practicalInformationSuggestions.constants';
import ICONS from 'src/Components/Shared/IconSelect/icons';
import useFetchAllPracticalInformation from 'src/Hooks/Queries/useFetchAllPracticalInformation';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { trackEvent } from 'src/Services/Matomo';
import { trackPracticalInformationSuggestionClicked } from 'src/Services/Mixpanel/events';

import styles from './PracticalInformationSuggestions.module.scss';

const STORAGE_KEY_PREFIX = 'practicalInfoSuggestionsCollapsed';

const readCollapsedFromStorage = (assetId: string | undefined): boolean => {
  if (!assetId) return false;
  try {
    return window.localStorage.getItem(`${STORAGE_KEY_PREFIX}-${assetId}`) === 'true';
  } catch {
    return false;
  }
};

const writeCollapsedToStorage = (assetId: string | undefined, collapsed: boolean) => {
  if (!assetId) return;
  try {
    window.localStorage.setItem(`${STORAGE_KEY_PREFIX}-${assetId}`, collapsed ? 'true' : 'false');
  } catch {
    // localStorage may be unavailable (private browsing) – fail silently.
  }
};

const renderIcon = (iconKey: string | undefined, size: number) => {
  if (!iconKey) return null;
  const IconComponent = ICONS[iconKey as keyof typeof ICONS];
  return IconComponent ? <IconComponent size={size} /> : null;
};

const PracticalInformationSuggestions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  const { data: allInformation } = useFetchAllPracticalInformation();
  const assetAboveQuotas = useIsAssetAboveQuotas();

  const [collapsed, setCollapsed] = useState(() => readCollapsedFromStorage(assetId));

  const toggleCollapsed = useCallback(() => {
    setCollapsed(previous => {
      const next = !previous;
      writeCollapsedToStorage(assetId, next);
      return next;
    });
  }, [assetId]);

  const existingSubtypes = useMemo(() => {
    const set = new Set<string>();
    (allInformation ?? []).forEach(information => {
      if (information.subtype) set.add(information.subtype);
    });
    return set;
  }, [allInformation]);

  const remainingSuggestions = useMemo(
    () =>
      PRACTICAL_INFORMATION_SUGGESTIONS.filter(
        suggestion => !existingSubtypes.has(suggestion.subtype)
      ),
    [existingSubtypes]
  );

  const handleFillClick = useCallback(
    (suggestion: PracticalInformationSuggestion) => {
      const type = getTypeForSubtype(suggestion.subtype);
      trackEvent('PracticalInformation', 'suggestion_click', suggestion.subtype);
      trackPracticalInformationSuggestionClicked({
        subtype: suggestion.subtype,
        type: type ?? 'UNKNOWN',
      });

      navigate('new', { state: { type, subtype: suggestion.subtype } });
    },
    [navigate]
  );

  if (remainingSuggestions.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.title}>
            {t('practicalInformation.suggestions.title', 'Les essentielles de votre maison')}
          </div>
          <Badge color="primary" variant="light">
            {t('practicalInformation.suggestions.counter', '{{ count }} à renseigner', {
              count: remainingSuggestions.length,
            })}
          </Badge>
        </div>
        <Button
          variant="subtle"
          size="xs"
          rightSection={collapsed ? <IconChevronDown size={16} /> : <IconChevronUp size={16} />}
          onClick={toggleCollapsed}
        >
          {collapsed
            ? t('practicalInformation.suggestions.show', 'Afficher')
            : t('practicalInformation.suggestions.hide', 'Masquer')}
        </Button>
      </div>
      <div className={styles.subtitle}>
        {t(
          'practicalInformation.suggestions.subtitle',
          "Les informations que chaque membre cherche un jour ou l'autre. Renseignez-les une fois, plus personne ne vous les redemandera."
        )}
      </div>
      <Collapse in={!collapsed}>
        <div className={styles.grid}>
          {remainingSuggestions.map(suggestion => {
            const type = getTypeForSubtype(suggestion.subtype);
            const subtypeIconKey = subtypeIcons[suggestion.subtype];
            const fallbackIconKey = type ? typeIcons[type] : undefined;
            const fillDisabled = assetAboveQuotas;

            return (
              <div key={suggestion.subtype} className={styles.card}>
                <div className={styles.cardTopLine}>
                  <div className={styles.cardIcon}>
                    {renderIcon(subtypeIconKey ?? fallbackIconKey, 20)}
                  </div>
                </div>
                <div className={styles.cardTitle}>
                  {t(suggestion.labelKey, suggestion.labelDefault)}
                </div>
                <div className={styles.cardDescription}>
                  {t(suggestion.descriptionKey, suggestion.descriptionDefault)}
                </div>
                <div className={styles.cardActions}>
                  <Tooltip
                    label={t(
                      'practicalInformation.suggestions.quotaReached',
                      'Limite de stockage atteinte.'
                    )}
                    disabled={!fillDisabled}
                  >
                    <Button
                      variant="light"
                      size="xs"
                      leftSection={<IconPencilPlus size={16} />}
                      onClick={() => handleFillClick(suggestion)}
                      disabled={fillDisabled}
                    >
                      {t('practicalInformation.suggestions.fill', 'Renseigner')}
                    </Button>
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      </Collapse>
    </div>
  );
};

export default PracticalInformationSuggestions;
